import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { AdminGuard } from "@/components/admin/AdminGuard.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import * as r0 from "@/api/karateR0LaunchCockpitApi.js";
import { ensureKarateR0Session } from "@/api/karateR0SessionBridge.js";
import { useAuth } from "@/context/AuthContext.jsx";
import "@/styles/launchCockpit.css";

const STATUS_OPTIONS = [
  { value: "TODO", labelFr: "À faire", labelEn: "To do" },
  { value: "IN_PROGRESS", labelFr: "En cours", labelEn: "In progress" },
  { value: "TO_VALIDATE", labelFr: "À valider", labelEn: "To validate" },
  { value: "DONE", labelFr: "Terminé", labelEn: "Done" },
  { value: "BLOCKED", labelFr: "Bloqué", labelEn: "Blocked" },
];

function formatDateRange(start, end, locale) {
  const opts = { day: "numeric", month: "short" };
  const s = new Date(`${start}T12:00:00`).toLocaleDateString(locale === "en" ? "en-CA" : "fr-CA", opts);
  const e = new Date(`${end}T12:00:00`).toLocaleDateString(locale === "en" ? "en-CA" : "fr-CA", opts);
  return `${s} — ${e}`;
}

export function LaunchCockpitContent() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ui, locale } = useKarateUi();
  const t = ui.adminPages?.launchCockpit ?? {};
  const guard = ui.guard;

  const [sessionReady, setSessionReady] = useState(import.meta.env.VITEST === true || import.meta.env.MODE === "test");
  const [sessionDenied, setSessionDenied] = useState(false);
  const [cockpit, setCockpit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [phase2Open, setPhase2Open] = useState(false);
  const [phase2Title, setPhase2Title] = useState("");
  const [phase2Description, setPhase2Description] = useState("");
  const [phase2Date, setPhase2Date] = useState("");

  const expandedFromUrl = searchParams.get("etape");
  const expandedStepId = expandedFromUrl?.startsWith("step-")
    ? expandedFromUrl
    : cockpit?.expandedStepId ?? "step-1";

  useEffect(() => {
    if (sessionReady || sessionDenied) return;
    let active = true;
    ensureKarateR0Session({ auth }).then((result) => {
      if (!active) return;
      if (result.ok) setSessionReady(true);
      else {
        setSessionDenied(true);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [auth, sessionReady, sessionDenied]);

  const loadCockpit = useCallback(async () => {
    if (!sessionReady) return;
    setLoading(true);
    const result = await r0.fetchLaunchCockpit();
    setLoading(false);
    if (result.status === 401) {
      setSessionDenied(true);
      return;
    }
    if (!result.ok) {
      setError(result.body?.error ?? t.loadError ?? "Impossible de charger le cockpit.");
      return;
    }
    setError("");
    setCockpit(result.body.cockpit);
  }, [sessionReady, t.loadError]);

  useEffect(() => {
    loadCockpit();
  }, [loadCockpit]);

  async function toggleStep(stepId) {
    const next = expandedStepId === stepId ? null : stepId;
    if (next) {
      navigate(`/admin/lancement-1er-septembre?etape=${next}`, { replace: true });
      setBusy(`step-${stepId}`);
      const result = await r0.setLaunchExpandedStep(stepId);
      setBusy(null);
      if (result.ok) setCockpit(result.body.cockpit);
    } else {
      navigate("/admin/lancement-1er-septembre", { replace: true });
    }
  }

  async function changeOutcome(outcomeId, status) {
    setBusy(outcomeId);
    const result = await r0.updateLaunchOutcome(outcomeId, status);
    setBusy(null);
    if (result.ok) setCockpit(result.body.cockpit);
    else setError(result.body?.error ?? t.updateError ?? "Mise à jour impossible.");
  }

  async function changeChecklist(checklistId, status) {
    setBusy(checklistId);
    const result = await r0.updateLaunchChecklist(checklistId, status);
    setBusy(null);
    if (result.ok) setCockpit(result.body.cockpit);
    else setError(result.body?.error ?? t.updateError ?? "Mise à jour impossible.");
  }

  async function changeDependency(id, status) {
    setBusy(id);
    const result = await r0.updateLaunchDependency(id, { status });
    setBusy(null);
    if (result.ok) setCockpit(result.body.cockpit);
  }

  async function submitPhase2(event) {
    event.preventDefault();
    if (!phase2Title.trim()) return;
    setBusy("phase2-add");
    const result = await r0.addLaunchPhase2Idea({
      title: phase2Title.trim(),
      description: phase2Description.trim() || undefined,
      date: phase2Date || undefined,
    });
    setBusy(null);
    if (result.ok) {
      setCockpit(result.body.cockpit);
      setPhase2Title("");
      setPhase2Description("");
      setPhase2Date("");
      setPhase2Open(false);
    }
  }

  async function deletePhase2(id) {
    setBusy(id);
    const result = await r0.removeLaunchPhase2Idea(id);
    setBusy(null);
    if (result.ok) setCockpit(result.body.cockpit);
  }

  if (sessionDenied) {
    return (
      <p data-testid="launch-cockpit-session-denied">
        {guard.denied} <Link to="/connexion">{guard.login}</Link>
      </p>
    );
  }

  if (!sessionReady || loading) {
    return <p data-testid="launch-cockpit-loading">{t.loading ?? "Chargement du cockpit…"}</p>;
  }

  if (!cockpit) {
    return <p className="form-error" role="alert">{error || (t.loadError ?? "Cockpit indisponible.")}</p>;
  }

  const deadlineClass = `launch-cockpit__deadline--${cockpit.deadlineStatus.toLowerCase()}`;

  return (
    <div className="launch-cockpit" data-testid="launch-cockpit-root">
      {error ? <p className="form-error" role="alert" data-testid="launch-cockpit-error">{error}</p> : null}

      <section className="launch-cockpit__summary" data-testid="launch-cockpit-summary">
        <div className="launch-cockpit__metric">
          <span className="launch-cockpit__metric-label">{t.countdown ?? "Compte à rebours"}</span>
          <span className="launch-cockpit__metric-value" data-testid="launch-countdown">
            {cockpit.countdownDays} {locale === "en" ? "days" : "jours"}
          </span>
        </div>
        <div className="launch-cockpit__metric">
          <span className="launch-cockpit__metric-label">{t.progress ?? "Progression"}</span>
          <span className="launch-cockpit__metric-value" data-testid="launch-progress">{cockpit.globalProgress}%</span>
        </div>
        <div className="launch-cockpit__metric">
          <span className="launch-cockpit__metric-label">{t.deadline ?? "Statut délai"}</span>
          <span className={`launch-cockpit__metric-value ${deadlineClass}`} data-testid="launch-deadline">
            {cockpit.deadlineLabel}
          </span>
        </div>
        <div className="launch-cockpit__metric">
          <span className="launch-cockpit__metric-label">{t.activeStep ?? "Étape active"}</span>
          <span className="launch-cockpit__metric-value" data-testid="launch-active-step">{cockpit.activeStepName}</span>
        </div>
        <div className="launch-cockpit__metric">
          <span className="launch-cockpit__metric-label">{t.priority ?? "Priorité actuelle"}</span>
          <span className="launch-cockpit__metric-value" data-testid="launch-priority">
            {cockpit.currentPriority ?? "—"}
          </span>
        </div>
        <div className="launch-cockpit__progress-bar" aria-hidden="true">
          <div className="launch-cockpit__progress-fill" style={{ width: `${cockpit.globalProgress}%` }} />
        </div>
      </section>

      <section className="launch-cockpit__now" data-testid="launch-cockpit-now">
        <h2>{t.nowTitle ?? "À faire maintenant"}</h2>
        {cockpit.nowActions.length === 0 ? (
          <p>{t.nowEmpty ?? "Aucune action urgente — continuez l'étape active."}</p>
        ) : (
          <ol className="launch-cockpit__now-list">
            {cockpit.nowActions.map((action) => (
              <li key={action.outcomeId}>
                <strong>{action.title}</strong> — {action.reason}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section data-testid="launch-cockpit-timeline">
        <h2>{t.timelineTitle ?? "Ligne du temps officielle"}</h2>
        <div className="launch-cockpit__timeline">
          {cockpit.steps.map((step) => {
            const open = expandedStepId === step.id;
            return (
              <article key={step.id} className="launch-cockpit__step" data-testid={`launch-step-${step.number}`}>
                <button
                  type="button"
                  className="launch-cockpit__step-header"
                  aria-expanded={open}
                  data-testid={`launch-step-toggle-${step.number}`}
                  onClick={() => toggleStep(step.id)}
                >
                  <strong>{t.stepLabel ?? "Étape"} {step.number}</strong>
                  <span>{formatDateRange(step.dateStart, step.dateEnd, locale)}</span>
                  <span>{step.name}</span>
                  <span>{step.progress}%</span>
                </button>
                {open ? (
                  <div className="launch-cockpit__step-body" data-testid={`launch-step-body-${step.number}`}>
                    {step.outcomes.map((outcome) => (
                      <div key={outcome.id} className="launch-cockpit__outcome" data-testid={`launch-outcome-${outcome.id}`}>
                        <div className="launch-cockpit__outcome-row">
                          <span>{outcome.label}</span>
                          <select
                            className="launch-cockpit__outcome-select"
                            value={outcome.status}
                            disabled={busy === outcome.id}
                            aria-label={`${t.statusFor ?? "Statut pour"} ${outcome.label}`}
                            data-testid={`launch-outcome-status-${outcome.id}`}
                            onChange={(e) => changeOutcome(outcome.id, e.target.value)}
                          >
                            {STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {locale === "en" ? opt.labelEn : opt.labelFr}
                              </option>
                            ))}
                          </select>
                        </div>
                        <small>
                          {t.responsible ?? "Responsable"} : {outcome.responsible}
                          {outcome.note ? ` — ${outcome.note}` : ""}
                        </small>
                      </div>
                    ))}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>

      <section
        className={`launch-cockpit__ready ${cockpit.readiness.ready ? "launch-cockpit__ready--yes" : "launch-cockpit__ready--no"}`}
        data-testid="launch-cockpit-readiness"
      >
        <h2>{t.readinessTitle ?? "Prêt pour le 1er septembre ?"}</h2>
        {cockpit.readiness.ready ? (
          <p data-testid="launch-ready-yes"><strong>🚀 {t.readyYes ?? "KARATÉ EST PRÊT À OUVRIR"}</strong></p>
        ) : (
          <p data-testid="launch-ready-no">
            {t.readyNo ?? "Pas encore prêt"} — {cockpit.readiness.unmetCriticalCount}{" "}
            {locale === "en" ? "critical condition(s) remain." : "condition(s) critique(s) reste(nt) à valider."}
          </p>
        )}
        <div className="launch-cockpit__checklist-grid">
          {cockpit.readiness.items.map((item) => (
            <div key={item.id} className="launch-cockpit__checklist-item" data-testid={`launch-check-${item.id}`}>
              <span>
                <small>{item.category}</small> — {item.label}
                {item.critical ? " *" : ""}
              </span>
              <select
                className="launch-cockpit__checklist-select"
                value={item.status}
                disabled={busy === item.id}
                aria-label={item.label}
                onChange={(e) => changeChecklist(item.id, e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {locale === "en" ? opt.labelEn : opt.labelFr}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="launch-cockpit-dependencies">
        <h2>{t.waitingTitle ?? "On attend de…"}</h2>
        <div className="launch-cockpit__deps-table-wrap">
          <table className="launch-cockpit__deps-table">
            <thead>
              <tr>
                <th>{t.colItem ?? "Élément"}</th>
                <th>{t.colResponsible ?? "Responsable"}</th>
                <th>{t.colStatus ?? "Statut"}</th>
                <th>{t.colNote ?? "Note"}</th>
              </tr>
            </thead>
            <tbody>
              {cockpit.dependencies.map((dep) => (
                <tr key={dep.id} data-testid={`launch-dep-${dep.id}`}>
                  <td>{dep.item}</td>
                  <td>{dep.responsible}</td>
                  <td>
                    <select
                      className="launch-cockpit__deps-select"
                      value={dep.status}
                      disabled={busy === dep.id}
                      aria-label={dep.item}
                      onChange={(e) => changeDependency(dep.id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {locale === "en" ? opt.labelEn : opt.labelFr}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{dep.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section
        className={`launch-cockpit__freeze ${cockpit.freezeActive ? "launch-cockpit__freeze--active" : ""}`}
        data-testid="launch-cockpit-freeze"
      >
        <h2>{t.freezeTitle ?? "Règle de gel V1"}</h2>
        <p>{cockpit.freezeRule}</p>
        {cockpit.freezeActive ? <p><strong>{t.freezeActive ?? "Gel actif — correctifs bloquants seulement."}</strong></p> : null}
      </section>

      <section data-testid="launch-cockpit-phase2">
        <h2>{t.phase2Title ?? "Phase 2 — après lancement"}</h2>
        {!phase2Open ? (
          <button type="button" className="btn btn--secondary" data-testid="launch-phase2-add-toggle" onClick={() => setPhase2Open(true)}>
            + {t.phase2Add ?? "Ajouter une idée"}
          </button>
        ) : (
          <form className="launch-cockpit__phase2-form" onSubmit={submitPhase2} data-testid="launch-phase2-form">
            <label>
              {t.phase2TitleLabel ?? "Titre"}
              <input value={phase2Title} onChange={(e) => setPhase2Title(e.target.value)} required data-testid="launch-phase2-title" />
            </label>
            <label>
              {t.phase2DescLabel ?? "Description (facultatif)"}
              <textarea value={phase2Description} onChange={(e) => setPhase2Description(e.target.value)} rows={2} data-testid="launch-phase2-desc" />
            </label>
            <label>
              {t.phase2DateLabel ?? "Date (facultatif)"}
              <input type="date" value={phase2Date} onChange={(e) => setPhase2Date(e.target.value)} data-testid="launch-phase2-date" />
            </label>
            <div>
              <button type="submit" className="btn btn--primary" disabled={busy === "phase2-add"} data-testid="launch-phase2-submit">
                {t.phase2Save ?? "Enregistrer"}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setPhase2Open(false)}>{t.cancel ?? "Annuler"}</button>
            </div>
          </form>
        )}
        <ul className="launch-cockpit__phase2-list">
          {cockpit.phase2Ideas.map((idea) => (
            <li key={idea.id} className="launch-cockpit__phase2-item" data-testid={`launch-phase2-${idea.id}`}>
              <div>
                <strong>{idea.title}</strong>
                {idea.description ? <p>{idea.description}</p> : null}
                {idea.date ? <small>{idea.date}</small> : null}
              </div>
              <button type="button" className="btn btn--ghost btn--sm" disabled={busy === idea.id} onClick={() => deletePhase2(idea.id)}>
                {t.remove ?? "Retirer"}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function LaunchCockpitPage() {
  const { ui, locale } = useKarateUi();
  const page = ui.adminPages?.launchCockpit ?? {};

  return (
    <AdminGuard>
      <PageSection
        title={page.pageTitle ?? (locale === "en" ? "🚀 September 1 launch" : "🚀 Lancement 1er septembre")}
        subtitle={page.pageSubtitle ?? (locale === "en" ? "Launch pilot cockpit — real status" : "Cockpit de pilotage — état réel")}
        testId="page-launch-cockpit"
      >
        <LaunchCockpitContent />
      </PageSection>
    </AdminGuard>
  );
}
